import { useState } from "react";
import { useTranslation } from "react-i18next";
import { EditorContent } from "@tiptap/react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@babackapp/ui/components/select";
import { FloatingPanelSeparator } from "@babackapp/ui/components/floating-panel";
import PanelContainer, {
  useBlockPanelPropsContext,
} from "../../lib/panel/panel-container";
import PanelContent from "../../lib/panel/panel-content";
import PanelTranslationState from "../../lib/panel/panel-translation-state";
import { EmailPanelToolbar } from "../paragraph/email-panel-toolbar";
import {
  INSTRUCTION_KEYS,
  type InstructionKey,
} from "../return-shipping-instructions/instructions";
import { useTitleEditor } from "./email-panel-editor";
import type { ReturnShippingInstructionsTitleBlockProps } from "./types";

/** Rich-text editor for the currently-selected title. */
function TitleEditor({ instructionKey }: { instructionKey: InstructionKey }) {
  const editor = useTitleEditor(instructionKey);
  return (
    <div className="space-y-2">
      <EmailPanelToolbar editor={editor} />
      <EditorContent editor={editor} />
    </div>
  );
}

function ReturnShippingInstructionsTitlePanelContent() {
  const { t } = useTranslation("editor-blocks-library");
  const { locale } = useBlockPanelPropsContext();
  const [selectedKey, setSelectedKey] =
    useState<InstructionKey>("customerCarrier");

  return (
    <>
      <PanelContent
        title={t(
          "editorBlocksLibrary.blocks.return-shipping-instructions-title.panel.message.title",
          "Title to customize",
        )}
      >
        {/* Dropdown options reuse the carrier labels from the message block. */}
        <Select
          value={selectedKey}
          onValueChange={(value) => setSelectedKey(value as InstructionKey)}
        >
          <SelectTrigger className="w-full h-8 text-sm bg-background">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {INSTRUCTION_KEYS.map((def) => (
              <SelectItem key={def.id} value={def.id}>
                {t(def.labelKey, def.labelFallback)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </PanelContent>
      <PanelContent
        title={t(
          "editorBlocksLibrary.blocks.return-shipping-instructions-title.panel.content.title",
          "Custom title",
        )}
      >
        {/* Remount on key OR locale change so the editor re-initializes both
            its content and its (locale-dependent) placeholder. */}
        <TitleEditor
          key={`${locale}:${selectedKey}`}
          instructionKey={selectedKey}
        />
      </PanelContent>
      <FloatingPanelSeparator />
      <PanelTranslationState mode="translatable" portal />
    </>
  );
}

export function ReturnShippingInstructionsTitlePanelRenderer(
  blockProps: ReturnShippingInstructionsTitleBlockProps,
) {
  return (
    <PanelContainer blockProps={blockProps}>
      <ReturnShippingInstructionsTitlePanelContent />
    </PanelContainer>
  );
}
